import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ApproveBooking() {
  const [params] = useSearchParams();
  const [state, setState] = useState('loading'); // loading | success | error
  const [booking, setBooking] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const error = params.get('error');
    const success = params.get('success');
    const token = params.get('token');
    const id = params.get('id');

    if (success === 'true' && id) {
      // Redirected back after successful approval
      fetch(`${API}/api/bookings/${id}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data) setBooking(data);
          setState('success');
        })
        .catch(() => setState('success'));
      return;
    }

    if (error) {
      setErrorMsg(error === 'invalid_token' ? 'This approval link has expired or already been used.' : 'Booking not found.');
      setState('error');
      return;
    }

    if (token && id) {
      // Call the approve endpoint (it will redirect back)
      window.location.href = `${API}/api/bookings/approve?token=${token}&id=${id}`;
      return;
    }

    setErrorMsg('Invalid approval link.');
    setState('error');
  }, [params]);

  return (
    <div className="min-h-screen bg-[#F4F5F6] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 max-w-sm w-full text-center" data-testid="approve-booking-page">
        {state === 'loading' && (
          <div>
            <Loader2 className="w-10 h-10 animate-spin text-[#0F8F8D] mx-auto mb-4" />
            <p className="text-gray-500">Approving booking...</p>
          </div>
        )}

        {state === 'success' && (
          <div>
            <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">Booking Approved</h1>
            {booking ? (
              <div className="text-sm text-gray-600 space-y-1 mt-4">
                <p><span className="font-medium">{booking.player_name}</span></p>
                <p>{booking.court_name} &middot; {booking.date}</p>
                <p className="font-medium text-gray-800">Status: Confirmed</p>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">The booking has been confirmed.</p>
            )}
            <a href="/operator" className="inline-block mt-6 px-6 py-2.5 bg-[#313638] text-white rounded-lg text-sm font-medium hover:bg-[#444] transition-colors" data-testid="go-to-dashboard-link">
              Go to Dashboard
            </a>
          </div>
        )}

        {state === 'error' && (
          <div>
            <XCircle className="w-14 h-14 text-status-held mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">Approval Failed</h1>
            <p className="text-gray-500 text-sm">{errorMsg}</p>
            <a href="/operator" className="inline-block mt-6 px-6 py-2.5 bg-[#313638] text-white rounded-lg text-sm font-medium hover:bg-[#444] transition-colors" data-testid="go-to-dashboard-link">
              Go to Dashboard
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
